import { withNamespaces } from "react-i18next";
import {
    Card,
    CardBody,
    Col,
    Container,
    Nav,
    NavItem,
    NavLink,
    Row,
    Spinner,
} from "reactstrap";
import OrganizationProductsService from "src/modules/3rd-party-management/apis/admin/OrganizationProductsService";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, { 
    PaginationProvider, 
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from 'react-bootstrap-table2-paginator';
import { EMPTY_LIST } from "src/common/constants";
import { useCallback, useEffect, useMemo, useState } from "react";
import CommonService from "src/modules/3rd-party-management/apis/common/CommonService";
import { useHistory, useLocation } from "react-router-dom";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import FilteringComponent from "./components/FilteringComponent";
import DateUtils from "src/services/utils/DateUtils";
import {
    INTERNATIONAL_DATE_FORMAT
} from 'src/common/constants';
import { DDSTypes } from "src/modules/3rd-party-management/constants";
import TableColumnCustomizer from "src/modules/3rd-party-management/components/TableColumnCustomizer";
import useTableColumnCustomizer from "src/modules/3rd-party-management/components/TableColumnCustomizer/CustomizerHook";
import Navigator from "src/components/Navigator";
import DocumentManager from "src/modules/3rd-party-management/components/document-management";

const eudrDefaultCols = [
    'id',
    'name',
    'productName',
    'supplier',
    'lotNumber',
    'internalProductId',
    'articleNumber',
    'internalSku',
    // 'importFromCountry',
    // 'exportToCountry',
    // 'importDate',   
    'metricTonnes',
    'is_eudr',
    // 'ddsReferenceNumber',
    // 'ddsVerificationNumber',
    'statementValidation',
    'hsCode',
    'knCode',
    // 'containerNumber',
    // 'shipmentNumber',
    // 'description',
    'actions'
];

const Products = ({
    t
}) => {
    const [filters, setFilters] = useState({
        pageIndex: 1,
        pageSize: 25,
        sortField: null,
        sortOrder: null
    });

    const history = useHistory();
    const location = useLocation();
    const dateUtils = new DateUtils();

    const {
        isFetching: productsAreFetching,
        isFetched: productsFetched,
        data: products
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-organization-products-list', 
            filters
        ],
        queryFn: async () => {
            const service = OrganizationProductsService.getInstance();

            return await service.list(filters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching products list.'), {
                type: 'error'
            });
        }
    });

    const handleFetchCountriesQuery = useQuery({
        queryKey: ['3rd-party-management-fetch-countries-list'],
        queryFn: async () => {
            const service = CommonService.getInstance();

            return await service.fetchCountries();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching locations.'), {
                type: 'error'
            });
        }
    });

    const eudrAllColumns = useMemo(() => {
        return [
            {
                dataField: "id",
                text: t("ID"),
                sort: true,
                style: {
                    width: '80px'
                }
            },
            {
                dataField: "name",
                text: t("NACE Code"),
                sort: true,
                style: {
                    width: '200px'
                },
                formatter: (cellContent) => {
                    return `${cellContent.class} - ${t(cellContent.name)}`
                }
            },
            {
                dataField: "productName",
                text: t("Product Name"),
                sort: true,
                style: {
                    width: '140px'
                }
            },
            {
                dataField: "supplier",
                text: t("Company"),
                sort: true,
                style: {
                    width: '120px'
                },
                formatter: (cellContent) => {
                    return cellContent?.name
                }
            },
            {
                dataField: "lotNumber",
                text: t("Lot Number"),
                sort: true
            },
            {
                dataField: "internalProductId",
                text: t("Internal Product ID"),
                sort: true
            },
            {
                dataField: "articleNumber",
                text: t("Article Number"),
                sort: false,
                locked: true,
                formatter: (_cell, row) => row?.articleNumber || row?.article_number || '---',
            },
            {
                dataField: "internalSku",
                text: t("Internal SKU"),
                sort: false,
                locked: true,
                formatter: (_cell, row) => row?.internalSku || row?.internal_sku || '---',
            },
            {
                dataField: "importDate",
                text: t("Date of Import"),
                sort: true,
                formatter: (cellContent) => {
                    return (
                        dateUtils.convertDateToDate(cellContent, INTERNATIONAL_DATE_FORMAT)
                    )
                }
            },
            {
                dataField: "importFromCountry",
                text: t("Import Country"),
                sort: true,
                formatter: (cellContent) => {
                    return t(cellContent?.name)
                }
            },
            {
                dataField: "exportDate",
                text: t("Date of Export"),
                sort: true,
                formatter: (cellContent) => {
                    return (
                        dateUtils.convertDateToDate(cellContent, INTERNATIONAL_DATE_FORMAT)
                    )
                }
            },
            {
                dataField: "exportToCountry",
                text: t("Export Country"),
                sort: true,
                formatter: (cellContent) => {
                    return t(cellContent?.name)
                }
            },
            {
                dataField: "metricTonnes",
                text: t("Quantity (Metric Tonnes)"),
                sort: true
            },
            {
                dataField: "originCountry",
                text: t("Country of Origin"),
                sort: true,
                formatter: (cellContent) => {
                    return t(cellContent?.name)
                }
            },
            {
                dataField: "ddsDeclarationType",
                text: t("DDS Declaration Type"),
                sort: false,
                formatter: (cellContent) => {
                    return t(DDSTypes[cellContent]?.title)
                }
            },
            {
                dataField: "is_eudr",
                text: t("EUDR Product"),
                sort: false,
                formatter: (cellContent) => {
                    if (!cellContent) {
                        return (
                            <span>
                                <i className="ri-close-line me-1 text-danger align-middle font-size-18" />
                                {t('No')}
                            </span>
                        )
                    }
                    return (
                        <span>
                            <i className="ri-checkbox-fill me-1 text-success align-middle font-size-18" />
                            {t('Yes')}
                        </span>
                    )
                }
            },
            ...(isFeatureEnabledForActiveModule('PPWR') ? [{
                dataField: "is_ppwr",
                text: t("PPWR Product"),
                sort: true,
                formatter: (cellContent) => {
                    if (!cellContent) {
                        return (
                            <span>
                                <i className="ri-close-line me-1 text-danger align-middle font-size-18" />
                                {t('No')}
                            </span>
                        )
                    }
                    return (
                        <span>
                            <i className="ri-checkbox-fill me-1 text-success align-middle font-size-18" />
                            {t('Yes')}
                        </span>
                    )
                }
            }] : []),
            {
                dataField: "ddsReferenceNumber",
                text: t("DDS Reference Number"),
                sort: true
            },
            {
                dataField: "ddsVerificationNumber",
                text: t("DDS Verification Number"),
                sort: true
            },
            {
                dataField: "statementValidation",
                text: t("DDS Statement Status"),
                sort: true,
                formatter: (cellContent, { ddsDeclarationType }) => {
                    if (ddsDeclarationType === 'provide_reference_numbers') {
                        if (cellContent === false) {
                            return (
                                <span>
                                    <i className="ri-close-line me-1 text-danger align-middle font-size-18" />
                                    {t('Invalid')}
                                </span>
                            )
                        }
                        else if (cellContent === true) {
                            return (
                                <span>
                                    <i className="ri-checkbox-fill me-1 text-success align-middle font-size-18" />
                                    {t('Valid')}
                                </span>
                            )
                        }
                        else {
                            return (
                                <span>
                                    {t('Validating')}...
                                </span>
                            )
                        }
                    }

                    return '-'
                }
            },
            {
                dataField: "geoStatus",
                text: t("Geo Status"),
                sort: false,
                formatter: (_cell, { geolocationReference }) => {
                    if(geolocationReference?.length > 0) {
                        return (
                            <span>
                                <i className="ri ri-check-line me-1 text-success font-size-20 align-middle" />
                                {t('Provided')}
                            </span>
                        )
                    }

                    return (
                        <span>
                            <i className="ri ri-close-line me-1 text-danger font-size-20 align-middle" />
                            {t('Not Provided')}
                        </span>
                    )
                }
            },
            {
                dataField: "hsCode",
                text: t("HS Code (6-digit)"),
                sort: false
            },
            {
                dataField: "knCode",
                text: t("KN Code (8-digit)"),
                sort: false
            },
            {
                dataField: "taricCode",
                text: t("TARIC Code (10-digit)"),
                sort: false
            },
            {
                dataField: "containerNumber",
                text: t("Container Number"),
                sort: true
            },
            {
                dataField: "shipmentNumber",
                text: t("Shipment Number"),
                sort: true
            },
            {
                dataField: "description",
                text: t('Description'),
                sort: false
            }
        ]
    }, [
        t,
        location.search,
        history
    ]);

    const {
        selectedColumns,
        onColumnsChange
    } = useTableColumnCustomizer({
        defaultSelectedCols: eudrDefaultCols,
        uniqueIdentifier: 'tpdd-admin-organization-products-cols'
    });

    const visibleColumns = useMemo(() => {
        if (isFeatureEnabledForActiveModule('eudr_activation')) {
            return eudrAllColumns.filter((col) => selectedColumns.includes(col.dataField) || col.locked)
        }

        return [
            {
                dataField: "id",
                text: t("ID"),
                sort: true,
                style: {
                    width: '80px'
                }
            },
            {
                dataField: "name",
                text: t("NACE Code"),
                sort: true,
                style: {
                    width: '200px'
                },
                formatter: (cellContent) => {
                    return `${cellContent.class} - ${t(cellContent.name)}`
                }
            },
            {
                dataField: "productName",
                text: t("Product Name"),
                sort: true,
                style: {
                    width: '200px'
                }
            },
            {
                dataField: "articleNumber",
                text: t("Article Number"),
                sort: false,
                formatter: (_cell, row) => row?.articleNumber || row?.article_number || '---',
            },
            {
                dataField: "internalSku",
                text: t("Internal SKU"),
                sort: false,
                formatter: (_cell, row) => row?.internalSku || row?.internal_sku || '---',
            },
            ...(isFeatureEnabledForActiveModule('PPWR') ? [{
                dataField: "is_ppwr",
                text: t("PPWR Product"),
                sort: true,
                style: {
                    width: '200px'
                },
                formatter: (cellContent) => {
                    if (!cellContent) {
                        return (
                            <span>
                                <i className="ri-close-line me-1 text-danger align-middle font-size-18" />
                                {t('No')}
                            </span>
                        )
                    }
                    return (
                        <span>
                            <i className="ri-checkbox-fill me-1 text-success align-middle font-size-18" />
                            {t('Yes')}
                        </span>
                    )
                }
            }] : []),
            {
                dataField: "supplier",
                text: t("Company"),
                sort: true,
                style: {
                    width: '200px'
                },
                formatter: (cellContent) => {
                    return cellContent?.name
                }
            },
            {
                dataField: "description",
                text: t('Description'),
                sort: false,
                style: {
                    width: '200px'
                }
            }
        ]
    }, [
        t,
        eudrAllColumns,
        selectedColumns
    ]);

    const handleTableFilterChange = useCallback((newFilters) => {
        setFilters((currentState) => {
            return {
                ...currentState,
                ...newFilters,
                pageIndex: 1
            }
        })
    }, []);

    const handleTableChange = (type, options) => {
        const {
            page,
            sizePerPage,

            sortField,
            sortOrder
        } = options;

        switch(type){
            case 'pagination':
                    setFilters({
                        ...filters,
                        pageIndex   :   page,
                        pageSize    :   sizePerPage
                    });
                break;
            case 'sort':
                    setFilters({
                        ...filters,
                        sortField   :   sortField,
                        sortOrder   :   sortOrder.toUpperCase()
                    })
                break;
            default:
                return false;
        }
    }

    const NoDataIndication = () => {
        if (productsFetched && !products?.length) {
            return (
                <div className="alert m-0" role="alert">
                    <p className="text-center mb-0">
                        {t(EMPTY_LIST)}
                    </p>
                </div>
            )
        }

        return <></>
    }

    const isEudrEnabled = isFeatureEnabledForActiveModule('eudr_activation');
    const activeTab = new URLSearchParams(location.search).get('tab');
    const tabs = useMemo(() => ([
        {
            key: 'products',
            title: 'Products',
        },
        {
            key: 'product-documents',
            title: 'Product Documents',
        },
    ]), []);

    useEffect(() => {
        if (!tabs.some((tab) => tab.key === activeTab)) {
            history.replace({
                pathname: location.pathname,
                search: '?tab=products',
            });
        }
    }, [activeTab, history, location.pathname, tabs]);

    return (
        <div className='page-content supplier-details-page'>
            <Container fluid className="mt-4 mb-4">
                <Row>
                    <Col lg="12" className="mb-4">
                        <Navigator
                            pageTitle={t('Products')}
                            translate={false}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col lg="12">
                        <div className="details-content-wrapper">
                            <div style={{ backgroundColor: '#D1D2D6' }}>
                                <Nav tabs className="custom-style-1">
                                    {tabs.map((tab) => (
                                        <NavItem
                                            key={tab.key}
                                            className={activeTab === tab.key ? 'active' : ''}
                                        >
                                            <NavLink
                                                className={activeTab === tab.key ? 'active' : ''}
                                                onClick={() => history.push({
                                                    pathname: location.pathname,
                                                    search: `?tab=${tab.key}`,
                                                })}
                                            >
                                                {t(tab.title)}
                                            </NavLink>
                                        </NavItem>
                                    ))}
                                </Nav>
                            </div>

                            <Card style={{ marginBottom: 0 }}>
                                <CardBody className="tabOverview">
                                    {activeTab === 'products' && (
                                        <>
                                            <Row>
                                                <Col sm="12" md="12" lg="3" className="mb-4">
                                                    {isEudrEnabled && (
                                                        <TableColumnCustomizer
                                                            allColumns={eudrAllColumns}
                                                            visibleColumns={selectedColumns}
                                                            onChange={onColumnsChange}
                                                        />
                                                    )}
                                                </Col>

                                                <Col sm="12" md="12" lg="9" className="d-flex justify-content-end mb-4">
                                                    <FilteringComponent
                                                        onChange={handleTableFilterChange}
                                                        countries={handleFetchCountriesQuery}
                                                    />
                                                </Col>
                                            </Row>
                                            <PaginationProvider
                                                pagination={
                                                    paginationFactory({
                                                        custom: true,
                                                        page: filters.pageIndex,
                                                        sizePerPage: filters.pageSize,
                                                        totalSize: parseInt(products?.itemsCount || 0),
                                                        withFirstAndLast: true,
                                                        alwaysShowAllBtns: true,
                                                        prePageText: <span><i className="ri-arrow-left-s-line"></i> {t('Back')}</span>,
                                                        nextPageText: <span>{t('Next')} <i className="ri-arrow-right-s-line"></i></span>,
                                                        prePageTitle: t('Pre page'),
                                                        firstPageTitle: t('First page'),
                                                        lastPageTitle: t('Last page'),
                                                        showTotal: false,
                                                        paginationTotalRenderer: (from, to, size) => {
                                                            return (
                                                                <span className="react-bootstrap-table-pagination-total">
                                                                    {`${t('Total Items')} ${size}`}
                                                                </span>
                                                            )
                                                        },
                                                        hideSizePerPage: false,
                                                        sizePerPageList: [
                                                            {
                                                                text: '25',
                                                                value: 25
                                                            },
                                                            {
                                                                text: '50',
                                                                value: 50
                                                            }
                                                        ]
                                                    })
                                                }>
                                                {
                                                    ({
                                                        paginationProps,
                                                        paginationTableProps
                                                    }) => (
                                                        <>
                                                            <Row>
                                                                <Col sm="12">
                                                                    <BootstrapTable
                                                                        remote={{
                                                                            pagination: true,
                                                                            filter: false,
                                                                            sort: true,
                                                                            cellEdit: false,
                                                                            search: false
                                                                        }}
                                                                        loading={productsAreFetching}
                                                                        overlay={overlayFactory({ spinner: <Spinner animation="border" variant="primary" size="md" />, text: `${t("Loading")}...` })}
                                                                        onTableChange={handleTableChange}
                                                                        defaultSorted={[]}
                                                                        keyField={"id"}
                                                                        responsive={true}
                                                                        bordered={false}
                                                                        data={products?.products || []}
                                                                        striped={true}
                                                                        columns={visibleColumns}
                                                                        wrapperClasses="table-responsive"
                                                                        classes={"table tpdd-table"}
                                                                        headerWrapperClasses={"thead-light"}
                                                                        noDataIndication={() => <NoDataIndication />}
                                                                        {...paginationTableProps}
                                                                    />
                                                                </Col>
                                                            </Row>

                                                            <Row>
                                                                <Col sm="12">
                                                                    <div className="tpdd-pagination-style-1">
                                                                        <PaginationTotalStandalone
                                                                            {...paginationProps}
                                                                        />

                                                                        <div>
                                                                            <PaginationListStandalone
                                                                                {...paginationProps}
                                                                            />

                                                                            <SizePerPageDropdownStandalone
                                                                                {...paginationProps}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </>
                                                    )
                                                }
                                            </PaginationProvider>
                                        </>
                                    )}

                                    {activeTab === 'product-documents' && (
                                        <DocumentManager linkedToProduct />
                                    )}
                                </CardBody>
                            </Card>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    )
}

export default withNamespaces()(
    Products
);